import * as React from 'react';
import lodash from 'lodash';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { Button, Checkbox, CircularProgress, FormControlLabel, FormGroup, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeviceSelect from '../components/DeviceSelect';
import Question from '../components/Question';
import FileUpload from '../components/FileUpload';
import Header, { VIEWS } from '../utils/Header';
import FormContext from '../context/FormContext';
import { parseText, FormText, isSystemLogsSupport } from '../utils/utils';
import SystemLogsModal from '../utils/SystemLogsModal';
import SubmitFormContext from '../context/SubmitFormContext';

import '../../styles/SubmitFormContainer.scss';
import EventManager from '../../libs/EventManager';

const FIELDS = {
    DEVICE: 'device',
    HEADER: 'header',
    EMAIL: 'email',
    TEXT: 'text',
    UPLOAD: 'upload',
    SYSTEM_LOGS: 'system_logs',
    TICKET_FIELD: 'ticket_field',
};

function capitalize (s) {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const parseLabels = (labels) => {
  const isNumber = /^-?\d+\.?\d*$/;
  return labels.reduce((acc, curr) => {
    const data = curr.split(':');
    const flag = data[0];
    if (data.length > 1) {
      if (isNumber.test(data[1])) {
        acc[flag] = Number(data[1]);
      } else if (data[1] === 'false') {
        acc[flag] = false;
      } else {
        acc[flag] = data[1];
      }
    } else {
      acc[flag] = true;
    }
    return acc;
  }, {});
}

const SystemLogs = ({
  systemLogs,
  onChange,
  values
}) => {
  const [open, setOpen] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const [content, setContent] = React.useState(null);

  React.useEffect(() => {
    if (!checked) {
      if (values[FIELDS.SYSTEM_LOGS]?.data) {
        setContent(null);
        onChange(FIELDS.SYSTEM_LOGS, FIELDS.SYSTEM_LOGS, null);
      }
    } else if (values && systemLogs) {
      let data = Object.keys(values).reduce((acc, id) => {
        const item = values[id];
        const isDevice = id.split('.').some(curr => {
          const array = curr.split('-');
          return array[0] === FIELDS.DEVICE;
        });
        if (isDevice) {
          const data = item.data;
          if (Array.isArray(data)) {
            data.forEach((device) => {
              const log = systemLogs[device.jid];
              if (log) {
                acc.push(log);
              }
            });
          } else {
            const log = systemLogs[data.jid];
            if (log) {
              acc.push(log);
            }
          }
        }
        return acc;
      }, []);
      data = lodash.uniqWith(data, (a, b) => a.jid === b.jid);
      if (data.length !== content?.length || 
        lodash.differenceWith(data, content, (a, b) => a.jid === b.jid).length > 0) {
        onChange(FIELDS.SYSTEM_LOGS, FIELDS.SYSTEM_LOGS, data);
        setContent(data);
      }
    }
  }, [systemLogs, values, checked]);


  function onCheckBoxChange (e) {
    setChecked(e.target.checked);
  }

  function onSystemLogsButtonClick () {
    setOpen(true);
    EventManager.clickSystemLog();
  }

  const systemLogsText = <Trans i18nKey="form.system_logs.text">
    I understand that some usage information will be sent to Alfred to better locate the issue you encountered. <Button sx={{ padding: 0 }} onClick={onSystemLogsButtonClick} disabled={!checked}>View my system logs</Button>.
  </Trans>
  return <>
    <FormGroup sx={{ borderTop: '1px solid #e1e1e1', paddingTop: '16px' }}>
      <FormControlLabel control={<Checkbox checked={checked} onChange={onCheckBoxChange} sx={{ marginTop: '-9px' }} />} label={systemLogsText} sx={{ alignItems: 'start' }} />
    </FormGroup>
    <SystemLogsModal open={open} onClose={() => setOpen(false)} content={content?.length > 0 ? JSON.stringify(content, undefined, 2) : t('form.system_logs.no_data') } />
  </>
}

const FormItem = ({ id = '', platform, feature, dataId, data, questions, onChange, values, token }) => {
  const title = data?.title;
  const desc = data?.body;
  const labels = data?.labels || {};
  const ticketFields = labels[FIELDS.TICKET_FIELD];
  switch (feature) {
    case FIELDS.DEVICE:
      const deviceProps = parseLabels(labels[FIELDS.DEVICE] || []);
      return <React.Fragment>
        <DeviceSelect
          placeholder={parseText(title)}
          {...deviceProps}
          token={token}
          value={values[dataId]?.data}
          onChange={(v) => onChange(dataId, id, v, ticketFields)}
        />
        <FormText data={parseText(desc)} className='desc' />
      </React.Fragment>;
    case FIELDS.HEADER:
      return <React.Fragment>
        <FormText data={parseText(title)} className='title' />
        <FormText data={parseText(desc)} className='desc' />
      </React.Fragment>
    case FIELDS.EMAIL:
      const emailProps = parseLabels(labels[FIELDS.EMAIL] || []);
      return <React.Fragment>
        <TextField
          type='email'
          variant='outlined'
          fullWidth
          placeholder={parseText(title)}
          {...emailProps}
          value={values[dataId]?.data || ''}
          onChange={(e) => onChange(dataId, id, e.target.value, ticketFields)}
        />
        <FormText data={parseText(desc)} className='desc' />
      </React.Fragment>;
    case FIELDS.TEXT:
      const textProps = parseLabels(labels[FIELDS.TEXT] || []);
      const { maxLength, minLength } = textProps;
      return <React.Fragment>
        <TextField
          variant='outlined'
          fullWidth
          placeholder={parseText(title)}
          inputProps={{
            minLength,
            maxLength,
          }}
          {...textProps}
          value={values[dataId]?.data || ''}
          onChange={(e) => onChange(dataId, id, e.target.value, ticketFields)}
        />
        <FormText data={parseText(desc)} className='desc' />
      </React.Fragment>;
    case FIELDS.UPLOAD:
      const uploadProps = parseLabels(labels[FIELDS.UPLOAD] || []);
      return <React.Fragment>
        <FileUpload
          placeholder={parseText(title)}
          platform={platform}
          {...uploadProps}
          token={token}
          value={values[dataId]?.data || []}
          onChange={(v) => onChange(dataId, id, v, ticketFields)}
        />
        <FormText data={parseText(desc)} className='desc' />
      </React.Fragment>;
    default:
      const question = questions.find(({ id }) => id.toString() === feature);
      if (!question) return null;
      return <FormItemGroup
        questions={questions}
        id={question.id}
        dataId={dataId}
        platform={platform}
        nodes={question.answers}
        name={question.name}
        description={question.description}
        onChange={onChange}
        values={values}
        token={token}
      />;
  }
}

function FormItemGroup ({ id = '', dataId = '', questions = [], nodes = [], name, description, onChange, values, token, platform }) {
  const { components, answers } = nodes.reduce((previous, current, answerIndex) => {
    const answerId = current.id;
    const features = current.labels['feature'] || [];
    const subComponents = features.map((feature, featureIndex) => <FormItem
      key={`${id}-${feature}-${answerId}`}
      dataId={`${dataId}.${answerIndex}.${feature}-${answerId}-${featureIndex}`}
      platform={platform}
      questions={questions}
      feature={feature}
      id={id}
      data={current}
      onChange={onChange}
      values={values}
      token={token}
    />).filter(Boolean);
    if (subComponents.length > 0) {
      previous['components'] = previous['components'].concat(subComponents);
    } else {
      previous['answers'].push(current);
    }
    return previous;
  }, { components: [], answers: [] });

  if (answers.length > 0) {
    const answerDataId = `${dataId}.${answers.length}.question-0`;
    return <div className='component'>
      <Question
        name={parseText(name)}
        items={answers}
        value={values[answerDataId]?.data || {}}
        onChange={(v, item) => onChange(answerDataId, id, v, item?.labels[FIELDS.TICKET_FIELD])}
      />
      {components}
    </div>
  } else {
    return <div className='component'>
      <FormText data={parseText(name)} className='title' />
      <FormText data={parseText(description)} className='desc' />
      {components}
    </div>
  }
}

export default function SubmitFormContainer(props) {
  const {
    questions = [],
    version,
    platform,
    data,
    systemLogs,
    token,
    onSubmit = async () => {},
    onClose
  } = props;
  const { form, onChange } = React.useContext(FormContext);
  const [values, setValues] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [systemLogsShow, setSystemLogsShow] = React.useState(true);
  const [pendingList, setPendingList] = React.useState({});

  React.useEffect(() => {
    if (data) {
      setSystemLogsShow(data?.labels[FIELDS.SYSTEM_LOGS] && isSystemLogsSupport(platform, version));
    }
  }, [data, platform, version]);

  React.useEffect(() => {
    if (form?.values) {
      setValues(form?.values);
    }
  }, [form]);

  async function handleSubmit (e) {
    e.preventDefault();
    setLoading(true);
    const keys = Object.keys(values);
    const submitData = keys.map(id => {
      const data = values[id];
      const question = questions.find(question => question.id === data.feature);
      const name = question?.name || id;
      const answer = data.data?.text || data.data
      if (name && answer) {
        const info = id.split('.').reduce((acc, curr) => {
          const array = curr.split('-');
          const priority = Number(array[array.length - 1])
          acc.priority.push(isNaN(priority) ? keys.length : priority);
          acc.type = array[0];
          return acc;
        }, { priority: [], type: null });
        return {
          priority: info.priority,
          type: info.type,
          question: parseText(name),
          answer: answer,
          fields: data.ticktFields,
        }
      }
      return null;
    }).filter(Boolean);
    submitData.sort(({ priority: a }, { priority: b }) => {
      const len = Math.max(a.length, b.length);
      for (let i = 0; i < len; i++) {
        if (a[i] > b[i]) {
          return 1;
        } else if (a[i] < b[i]) {
          return -1;
        }
      }
      return 0;
    })
    let contactEmail;
    submitData.forEach(item => {
      delete item.priority;
      switch (item.type) {
        case FIELDS.DEVICE:
          if (Array.isArray(item.answer)) {
            item.answer = item.answer.map((device) => {
              const str = [capitalize(device.type), device.device, device.app_build, `${device.os} ${device.os_version}`];
              return `${device.name} (${str.filter(Boolean).join(' / ')})`;
            });
          } else if (typeof item.answer === 'object') {
            const device = item.answer;
            const str = [capitalize(device.type), device.device, device.app_build, `${device.os} ${device.os_version}`];
            item.answer = [`${device.name} (${str.filter(Boolean).join(' / ')})`];
          } else {
            item.answer = [item.answer];
          }
          break;
        case FIELDS.UPLOAD:
          if (!Array.isArray(item.answer)) break;
          item.answer = item.answer.map(data => data.data);
          break;
        case FIELDS.EMAIL:
          contactEmail = item.answer;
          break;
        default:
          break;
      }
    });
    const ticketId = await onSubmit(values, submitData, data?.labels['tag'], contactEmail);
    EventManager.clickSend(ticketId, values[FIELDS.SYSTEM_LOGS]? (values[FIELDS.SYSTEM_LOGS].data !== null) : null);
    setLoading(false);
  }

  function handleChange (dataId, feature, data, ticktFields) {
    const newValues = {
      [dataId]: {
        feature,
        data,
        ticktFields
      },
    };
    setValues((preValues) => {
      return { ...preValues, ...newValues };
    });
    onChange({ values: newValues });
  }

  function handleClose () {
    setValues({})
    onChange({ values: {} });
    onClose();
  }

  const features = data?.labels['feature'] || [];
  const systemLogsData = values[FIELDS.SYSTEM_LOGS]?.data;
  const buttonDisabed = loading || (systemLogsShow && !systemLogsData) || Object.keys(pendingList).length > 0;

  return <SubmitFormContext.Provider value={{ pending: (id) => setPendingList((list) => ({ ...list, [id]: true })), resolve: (id) => setPendingList((list) => {
    delete list[id];
    return { ...list };
  })}}>
    <form className='submit-form-container' onSubmit={handleSubmit}>
      <Header title={t('header')} view={VIEWS.FORM} backIcon={<ArrowBackIcon />} onBack={handleClose}/>
      <div className='submit-form-body'>
        {
          features.map((feature, index) =>
            <FormItem
              key={`${feature}-${index}`}
              platform={platform}
              dataId={`${feature}-${index}`}
              feature={feature}
              index={index}
              questions={questions}
              onChange={handleChange}
              values={values}
              token={token}
            />)
        }
        {systemLogsShow && <SystemLogs systemLogs={systemLogs} onChange={handleChange} values={values} />}
      </div>
      <div className='submit-form-footer'>
        <Button variant='contained' type='submit' sx={{ color: '#1A1A1A', fontSize: '19px', height: '48px' }} fullWidth disabled={buttonDisabed}>{loading ?  <CircularProgress size={24} color='normal' sx={{ padding: 0.5 }} /> : t('form.send')}</Button>
      </div>
    </form>
  </SubmitFormContext.Provider>
}